/**
 * Citadel Dashboard — admin surface index.
 *
 * Sprint: SPR-26.O | BL-29-1790
 * See docs/architecture/Citadel_Architecture_ADR.md
 */
import { Link } from 'react-router-dom';
// Synthesis XII-5: Phosphor (thin) — Citadel ops surface, cartographic-instrument feel.
// ShieldAlert → ShieldWarning, LayoutTemplate → Layout (closest semantic equivalents).
import {
  ToggleLeft,
  Layout as LayoutTemplate,
  ShieldWarning as ShieldAlert,
  CaretRight as ChevronRight,
} from 'phosphor-react';
import { useAccessTokenSync } from '@/lib/auth';

interface Surface {
    href: string;
    label: string;
    description: string;
    icon: typeof ToggleLeft;
    status: 'live' | 'planned';
}

const SURFACES: Surface[] = [
    {
        href: '/feature-switches',
        label: 'Feature switches',
        description:
            'Master controls for platform-wide feature flags. Toggles forensics-logged.',
        icon: ToggleLeft,
        status: 'live',
    },
    {
        href: '/templates',
        label: 'Templates Moderation',
        description:
            'Review and ratify templates submitted to the Noeura marketplace. Three-layer governance: Noeura imagines → Citadel ratifies → Eidolon recommends.',
        icon: LayoutTemplate,
        status: 'live',
    },
    {
        href: '/run-status-reconciliation',
        label: 'Trust integrity — Run-Status Reconciliation',
        description:
            'Surfaces orbiset runs that report SUCCEEDED while at least one child step is FAILED. A non-zero count is a critical trust-integrity signal (BL-29-2170).',
        icon: ShieldAlert,
        status: 'live',
    },
];

export default function Dashboard() {
    useAccessTokenSync();
    return (
        <div className="space-y-6">
            <header>
                <h1 className="text-lg font-semibold tracking-tight">
                    Admin plane
                </h1>
                <p className="mt-1 text-xs text-citadel-ink-dim font-mono">
                    citadel.rodiun.io — operational independence from
                    app.rodiun.io
                </p>
            </header>

            <ul className="grid gap-2">
                {SURFACES.map(({ href, label, description, icon: Icon, status }) => (
                    <li key={href}>
                        <Link
                            to={href}
                            className="flex items-start justify-between gap-4 border border-citadel-border bg-citadel-surface hover:border-citadel-border-strong hover:bg-citadel-surface-2 p-4 transition-colors group"
                        >
                            <div className="flex items-start gap-3 min-w-0">
                                <Icon
                                    className="size-4 text-citadel-cyan mt-0.5 shrink-0"
                                    weight="thin"
                                    aria-hidden
                                />
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-sm font-semibold text-citadel-ink">
                                            {label}
                                        </h2>
                                        <span
                                            className={`text-2xs font-mono uppercase tracking-wider px-1.5 py-0.5 ${
                                                status === 'live'
                                                    ? 'bg-citadel-success/10 text-citadel-success'
                                                    : 'bg-citadel-amber/10 text-citadel-amber'
                                            }`}
                                        >
                                            {status}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-xs text-citadel-ink-dim leading-relaxed">
                                        {description}
                                    </p>
                                </div>
                            </div>
                            <ChevronRight
                                className="size-4 text-citadel-ink-faint group-hover:text-citadel-ink mt-1 shrink-0"
                                weight="thin"
                                aria-hidden
                            />
                        </Link>
                    </li>
                ))}
            </ul>

            <footer className="pt-4 border-t border-citadel-border text-2xs font-mono text-citadel-ink-faint">
                <p>
                    Citadel is the operational plane for Rodiun infrastructure.
                    All actions are audit-logged. See{' '}
                    <code className="text-citadel-ink-dim">
                        docs/architecture/Citadel_Architecture_ADR.md
                    </code>
                    .
                </p>
            </footer>
        </div>
    );
}
